import React, { useState } from 'react';

import { usePostVerifyCode } from 'apis/handler/auth/usePostVerifyCode';
import Button from 'components/button/Button';
import FixedLoading from 'components/common/FixedLoading';
import InputContainer from 'components/input/InputContainer';
import ContentModalHeader from 'components/modal/ContentModalHeader';
import { usePostEmailCertification } from 'features/auth';
import { Trans, useTranslation } from 'react-i18next';
import { removeWhitespace } from 'shared/lib';

import { trackSignupFieldError } from '../../lib';

const GMAIL_DOMAINS = ['gmail.com', 'googlemail.com'];

interface SignupStep2Props {
  email: string;
  onClickNext: () => void;
}

const SignupStep2 = ({ email, onClickNext }: SignupStep2Props) => {
  const { t } = useTranslation('auth');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const isGmail = GMAIL_DOMAINS.includes(email.split('@')[1]?.toLowerCase() ?? '');

  const { mutateAsync: tryVerifyCode, isPending: verifyCodeIsPending } = usePostVerifyCode({
    onSuccess: () => {
      window.dataLayer?.push({
        event: 'sign_up_step2',
      });
      onClickNext();
    },

    onError: (error) => {
      if (error === '인증에 실패하였습니다.') {
        setCodeError(t('signup.step2.codeIncorrect'));
        trackSignupFieldError(2, 'code_incorrect');
      } else {
        setCodeError(error);
      }
    },
  });

  const { mutateAsync: trySendEmailCertification, isPending: sendEmailIsPending } = usePostEmailCertification({
    onSuccess: () => {
      // console.log(data);
    },
    onError: (error) => {
      if (error === '중복된 이메일입니다.') {
        alert(t('signup.step1.accountExistsError'));
      } else {
        alert(error);
      }
    },
  });

  const onChangeCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCodeError('');

    setCode(removeWhitespace(e.target.value, 'NUM'));
  };

  const handleResendEmail = async () => {
    if (sendEmailIsPending) return;
    try {
      await trySendEmailCertification(email);
    } catch (error) {
      console.error(error);
    }
  };

  const handleNext = async () => {
    if (verifyCodeIsPending) return;

    if (code === '' || code.length < 6) {
      setCodeError(t('signup.step2.codeIncorrect'));
      trackSignupFieldError(2, 'code');
      return;
    }

    try {
      await tryVerifyCode({ email, code });
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenGmail = () => {
    window.dataLayer?.push({ event: 'sign_up_open_gmail' });
    window.open('https://mail.google.com/mail/u/0/#inbox', '_blank');
  };

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNext();
    }
  };

  return (
    <div className="flex w-[309px] max-w-full flex-col gap-y-4 px-5 py-5 md:mx-45 md:my-6 md:gap-y-5 md:p-0">
      <div className="flex w-full flex-col items-center gap-y-4">
        <ContentModalHeader
          src="/images/optimized/illusts/il_mail.webp"
          title={t('signup.title')}
          description={`${t('signup.step2.verificationSent')} ${email}.\n${t('signup.step2.checkInbox')}`}
        />
        <InputContainer
          label={t('signup.step2.verificationCodeLabel')}
          placeholder={t('signup.step2.verificationCodePlaceholder')}
          error={codeError}
          value={code}
          onChange={onChangeCode}
          onKeyPress={onKeyPress}
          maxLength={6}
          autoComplete="one-time-code"
          inputMode="numeric"
        />
        {isGmail && (
          <Button text={t('signup.step2.openGmail')} shape="SQUARE" theme="PRIMARY_OUTLINE" onClick={handleOpenGmail} />
        )}
        <div className="w-full">
          <p className="mt-1 pt-px text-center text-c2">
            {t('signup.step2.didntReceiveEmail')}{' '}
            <span
              className="cursor-pointer text-primary underline-offset-2 hover:underline"
              onClick={handleResendEmail}
            >
              {t('signup.step2.resendEmail')}
            </span>
          </p>

          <div className="text-center text-c2 text-gray-40">
            <Trans i18nKey="auth:signup.step2.spamFolderNote" components={{ br: <br /> }} />
          </div>
        </div>
      </div>
      <Button text={t('signup.step2.continue')} shape="SQUARE" onClick={handleNext} disabled={verifyCodeIsPending} />
      {(verifyCodeIsPending || sendEmailIsPending) && <FixedLoading />}
    </div>
  );
};

export default SignupStep2;
